import { api } from "#/lib/api-client";
import type { EmployeeDetails } from "#/features/employees/types";
import type {
  ClaimCreateRequest,
  ClaimDecisionRequest,
  ClaimFilters,
  ClaimLineCreateRequest,
  ClaimLineUpdateRequest,
  ClaimStats,
  ClaimUpdateRequest,
  ExpenseClaimDetails,
  ExpenseClaimLine,
  ExpenseClaimSummary,
  PageResponse,
} from "./types";

/**
 * Normalises a raw backend response into a consistent PageResponse shape.
 *
 * The claims endpoints (/me, /inbox, /) may return either a flat Spring Page:
 *   { content: [...], totalElements: N, totalPages: N, ... }
 * or an envelope-wrapped variant:
 *   { data: { content: [...], totalElements: N, ... } }
 *
 * We detect wrapping by checking whether `content` is directly present.
 * We also handle snake_case aliases (`total_elements`, `total_pages`) for
 * backends that serialise without camelCase conversion.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizePage<T>(raw: any): PageResponse<T> {
  // Spring Boot 3.3+ nests pagination metadata under a `page` key:
  //   { content: [...], page: { size, number, totalElements, totalPages } }
  // Older versions return it flat:
  //   { content: [...], totalElements, totalPages, number, size, ... }
  // We also handle an optional envelope wrapper: { data: { content, ... } }
  const body = raw?.content !== undefined ? raw : (raw?.data ?? raw);
  const meta = body?.page ?? body; // prefer nested `page` object, fall back to flat
  return {
    content:       body?.content       ?? [],
    totalElements: meta?.totalElements ?? meta?.total_elements ?? meta?.total ?? 0,
    totalPages:    meta?.totalPages    ?? meta?.total_pages    ?? 0,
    number:        meta?.number        ?? 0,
    size:          meta?.size          ?? 0,
    first:         body?.first  ?? meta?.number === 0,
    last:          body?.last   ?? (meta?.number ?? 0) + 1 >= (meta?.totalPages ?? meta?.total_pages ?? 1),
    empty:         body?.empty  ?? (body?.content?.length === 0),
  };
}

function buildParams(filters?: ClaimFilters) {
  const dateFrom = filters?.dateFrom ?? (filters?.year ? `${filters.year}-01-01` : undefined);
  const dateTo   = filters?.dateTo   ?? (filters?.year ? `${filters.year}-12-31` : undefined);

  return {
    page: filters?.page ?? 0,
    size: filters?.size ?? 10,
    ...(filters?.status     && { status: filters.status }),
    ...(filters?.claimType  && { claimType: filters.claimType }),
    ...(filters?.employeeId && { employeeId: filters.employeeId }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo   && { dateTo }),
    ...(filters?.search     && { search: filters.search }),
  };
}

export async function fetchClaimsStats() {
  const { data } = await api.get<ClaimStats>("/api/hr/claims/stats");
  return data;
}

export async function fetchCurrentEmployee() {
  const { data } = await api.get<EmployeeDetails>("/api/hr/employees/me");
  return data;
}

export async function fetchMyClaims(filters?: ClaimFilters) {
  const { data } = await api.get("/api/hr/claims/me", { params: buildParams(filters) });
  return normalizePage<ExpenseClaimSummary>(data);
}

export async function fetchClaimsForApproval(filters?: ClaimFilters) {
  const { data } = await api.get("/api/hr/claims/inbox", { params: buildParams(filters) });
  return normalizePage<ExpenseClaimSummary>(data);
}

export async function fetchAllClaims(filters?: ClaimFilters) {
  const { data } = await api.get("/api/hr/claims", { params: buildParams(filters) });
  return normalizePage<ExpenseClaimSummary>(data);
}

export async function fetchClaimById(id: number) {
  const { data } = await api.get<ExpenseClaimDetails>(`/api/hr/claims/${id}`);
  return data;
}

export async function createClaim(payload: ClaimCreateRequest) {
  const { data } = await api.post<ExpenseClaimDetails>(
    "/api/hr/claims",
    payload,
  );
  return data;
}

export async function updateClaim(id: number, payload: ClaimUpdateRequest) {
  const { data } = await api.put<ExpenseClaimDetails>(
    `/api/hr/claims/${id}`,
    payload,
  );
  return data;
}

export async function addClaimLine(id: number, payload: ClaimLineCreateRequest) {
  const { data } = await api.post<ExpenseClaimLine>(
    `/api/hr/claims/${id}/lines`,
    payload,
  );
  return data;
}

export async function updateClaimLine(
  claimId: number,
  lineId: number,
  payload: ClaimLineUpdateRequest,
) {
  const { data } = await api.put<ExpenseClaimLine>(
    `/api/hr/claims/${claimId}/lines/${lineId}`,
    payload,
  );
  return data;
}

export async function deleteClaimLine(claimId: number, lineId: number) {
  await api.delete(`/api/hr/claims/${claimId}/lines/${lineId}`);
}

export async function submitClaim(id: number) {
  const { data } = await api.post<ExpenseClaimDetails>(
    `/api/hr/claims/${id}/submit`,
  );
  return data;
}

export async function approveClaim(id: number, payload: ClaimDecisionRequest) {
  const { data } = await api.post<ExpenseClaimDetails>(
    `/api/hr/claims/${id}/approve`,
    payload,
  );
  return data;
}

export async function rejectClaim(id: number, payload: ClaimDecisionRequest) {
  const { data } = await api.post<ExpenseClaimDetails>(
    `/api/hr/claims/${id}/reject`,
    payload,
  );
  return data;
}

export async function cancelClaim(id: number, comments?: string) {
  const { data } = await api.post<ExpenseClaimDetails>(
    `/api/hr/claims/${id}/cancel`,
    { comments },
  );
  return data;
}

export async function markClaimPaid(id: number) {
  const { data } = await api.post<ExpenseClaimDetails>(`/api/hr/claims/${id}/pay`);
  return data;
}

export async function uploadClaimDocument(
  claimId: number,
  file: File,
  documentType?: string,
  lineId?: number,
) {
  const formData = new FormData();
  formData.append("file", file);
  if (documentType?.trim()) {
    formData.append("documentType", documentType.trim());
  }
  if (lineId != null) {
    formData.append("lineId", String(lineId));
  }

  const { data } = await api.post(
    `/api/hr/claims/${claimId}/documents`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );

  return data;
}

export async function deleteClaimDocument(claimId: number, documentId: number) {
  await api.delete(`/api/hr/claims/${claimId}/documents/${documentId}`);
}

export async function downloadClaimDocument(claimId: number, documentId: number) {
  const { data, headers } = await api.get(
    `/api/hr/claims/${claimId}/documents/${documentId}/download`,
    { responseType: "blob" },
  );
  const contentType: string = headers["content-type"] ?? "application/octet-stream";
  return { blob: data as Blob, contentType };
}